import { useEffect, useRef } from 'react';

/**
 * Care-mode entry: a still long-press in the top-right corner, **or** five taps there.
 *
 * Three properties of the hold, each load-bearing:
 *
 *  - **In the corner, outside the content band.** The output bar occupies the middle 60 % of the
 *    width, so the top corners are empty on every surface. Nothing to collide with, and no visible
 *    control for the person to wonder about.
 *  - **Still.** The press is cancelled by movement, so a hand resting on the bezel while the iPad
 *    is held does not open care mode. The person holds this device; an accidental entry would drop
 *    them into a screen full of settings mid-conversation.
 *  - **Independent of the active input adapter.** This does not go through `InputAdapter`. If care
 *    mode were reachable only through the adapter, a caregiver who selected an input mode that does
 *    not work for this person would have no way back in to undo it — the app would be bricked from
 *    their point of view.
 *
 * ── Why there is a second gesture ──
 *
 * A tester could not open care mode at all on their device, and the hold has no shortage of ways to
 * fail on real glass: a finger drifting more than {@link MOVE_TOLERANCE_PX} over two seconds, a
 * `pointercancel` from a system edge gesture in the very band the corner occupies, a second finger
 * anywhere on the screen, a hovering Apple Pencil. **Five taps need nothing but `pointerdown`**, so it
 * survives all of them. The hold is unchanged and still documented — this is a second route, not a
 * replacement.
 *
 * ── Why the handlers belong to an element ──
 *
 * They used to be four `document` listeners with a `closest('[data-activate-id]')` bail-out, and that
 * combination could make care mode **permanently unreachable**: `contentBandPct` is a care-mode slider
 * that goes to 100, and past about 65 % the output bar — which is an `ActivateButton` — covers this
 * corner and swallows every press. Calibration's own output (a 50 % band offset by 15 %) reaches the
 * same state. Owning a transparent element that sits above the band fixes that, and three other
 * things fall out of it: the corner is in viewport coordinates so it survives Partner-Ansicht's
 * `rotate(180deg)`, capturing one `pointerId` means another finger cannot cancel a hold in progress,
 * and stopping propagation keeps a corner press in scanning mode from doubling as a switch press on
 * whatever happens to be highlighted.
 */

const HOLD_MS = 2000;
const MOVE_TOLERANCE_PX = 12;

/** Fraction of the viewport, from the top-right, that counts as the corner. */
export const CORNER_FRACTION = 0.18;

/** Taps needed, and the window they must fall inside. Five is far more than a stray double-tap. */
const TAP_COUNT = 5;
const TAP_WINDOW_MS = 4000;

export interface CornerGestureHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
}

export function useCornerLongPress(onTrigger: () => void): CornerGestureHandlers {
  /**
   * Through a ref so the handlers never go stale without being rebuilt.
   *
   * `App` passes a fresh arrow every render, and the previous implementation depended on that identity
   * in an effect — so any re-render during the two-second hold tore the listeners down and silently
   * lost the press. It happened not to fire, because `App` subscribes to little; it was one new
   * subscription away from breaking care-mode entry with no test to catch it.
   */
  const trigger = useRef(onTrigger);
  trigger.current = onTrigger;

  const hold = useRef<{ timer: ReturnType<typeof setTimeout>; pointerId: number; x: number; y: number } | null>(null);
  /** Timestamps of recent corner taps, newest last, pruned to the window on every press. */
  const taps = useRef<number[]>([]);

  const cancelHold = (): void => {
    if (hold.current) clearTimeout(hold.current.timer);
    hold.current = null;
  };

  // A hold resolved by `setTimeout` after the app went away would drop a caregiver into care mode on
  // return, having forgotten they ever pressed.
  useEffect(() => {
    const onHidden = (): void => {
      if (document.visibilityState === 'hidden') cancelHold();
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      cancelHold();
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, []);

  const fire = (): void => {
    cancelHold();
    taps.current = [];
    trigger.current();
  };

  return {
    onPointerDown: (event) => {
      // The person's own input never reaches here — the element is empty and carries no
      // `data-activate-id` — so nothing is taken away by keeping the event to ourselves. In scanning
      // mode this is what stops a corner press registering as a switch press.
      event.stopPropagation();

      // One pointer at a time. `isPrimary` keeps a second finger from replacing a hold in progress,
      // which used to overwrite the timer and leak the first one.
      if (!event.isPrimary || hold.current) return;

      const now = event.timeStamp;
      taps.current = [...taps.current.filter((at) => now - at < TAP_WINDOW_MS), now];
      if (taps.current.length >= TAP_COUNT) {
        fire();
        return;
      }

      // Capture, so only this pointer's moves are delivered here and a finger elsewhere on the glass
      // cannot cancel the hold. The checklist requires entry to work while the iPad is being held.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Capture is an optimisation, not a requirement; the hold still works without it.
      }

      hold.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        timer: setTimeout(fire, HOLD_MS),
      };
    },

    onPointerMove: (event) => {
      const active = hold.current;
      if (!active || event.pointerId !== active.pointerId) return;
      // A hovering pencil or a mouse crossing the corner emits moves without contact, and neither is
      // the person's hand slipping.
      if (event.pressure === 0 && event.buttons === 0) return;

      const moved =
        Math.abs(event.clientX - active.x) > MOVE_TOLERANCE_PX ||
        Math.abs(event.clientY - active.y) > MOVE_TOLERANCE_PX;
      if (moved) cancelHold();
    },

    onPointerUp: (event) => {
      if (hold.current && event.pointerId !== hold.current.pointerId) return;
      cancelHold();
    },

    onPointerCancel: (event) => {
      if (hold.current && event.pointerId !== hold.current.pointerId) return;
      cancelHold();
    },
  };
}
